// Bertsy Embed — schlanker "Mit Bertsy verbinden"-Button für Praxis-Webseiten.
//
// Funktionsweise:
//   1. Praxis bindet das Script auf ihrer Webseite ein und übergibt ihren Praxis-Code:
//      <script src="https://cdn.bertsy.de/bertsy-embed.js"
//              data-praxis-code="K3v9XmpA2rTb..."
//              data-position="bottom-right"></script>
//
//   2. Auf der Webseite erscheint ein klickbarer Bertsy-Button.
//
//   3. Beim Klick:
//      - Wenn Mobile + Bertsy-App installiert → öffnet App via Deep-Link mit Praxis-Code
//      - Wenn Mobile ohne App → leitet zum App-Store (mit Praxis-Code als Hash)
//      - Wenn Desktop → zeigt QR-Code-Modal zum Scannen
//
// Praxis muss nichts weiter tun. Bertsy-Plattform kümmert sich um den Rest.
//
// Konfigurations-Optionen (alle als data-Attribute am Script-Tag):
//   data-praxis-code (REQUIRED)  — der vollständige Praxis-Code aus dem Admin-Panel
//   data-position (optional)     — bottom-right | bottom-left (default: bottom-right)
//   data-color (optional)        — Primary-Color für den Button (default: #00A099)
//   data-label (optional)        — Tooltip-Text (default: "Mit Bertsy verbinden")

import './styles.css';
import logoSvg from '../../../content/bertsy-logo.svg?raw';

declare global {
  interface Window {
    Bertsy?: {
      open: () => void;
    };
  }
}

interface ScriptOptions {
  praxisCode?: string;
  position?: string;
  color?: string;
  label?: string;
}

const APP_STORE_URL_IOS = 'https://apps.apple.com/de/app/bertsy/idTBD';
const APP_STORE_URL_ANDROID = 'https://play.google.com/store/apps/details?id=de.bertsy.app';
const DEEP_LINK_SCHEME = 'bertsy://verbinden';
const UNIVERSAL_LINK = 'https://bertsy.de/verbinden';

function getScriptOptions(): ScriptOptions {
  const scripts = document.querySelectorAll('script[src*="bertsy-embed"]');
  const last = scripts[scripts.length - 1] as HTMLScriptElement | undefined;
  if (!last) return {};
  return {
    praxisCode: last.dataset.praxisCode || last.dataset.code,
    position: last.dataset.position,
    color: last.dataset.color,
    label: last.dataset.label,
  };
}

function isMobile(): boolean {
  return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
}

function isIOS(): boolean {
  return /iPhone|iPad|iPod/i.test(navigator.userAgent);
}

function buildDeepLink(praxisCode: string): string {
  return `${DEEP_LINK_SCHEME}?code=${encodeURIComponent(praxisCode)}`;
}

function buildUniversalLink(praxisCode: string): string {
  // Universal Link funktioniert auf iOS auch, wenn App nicht installiert
  // (fallback öffnet bertsy.de mit dem Code als Hash).
  return `${UNIVERSAL_LINK}?code=${encodeURIComponent(praxisCode)}`;
}

function appStoreUrl(): string {
  return isIOS() ? APP_STORE_URL_IOS : APP_STORE_URL_ANDROID;
}

function openOnMobile(praxisCode: string): void {
  // Strategie:
  // 1. Versuche Universal Link / Deep Link
  // 2. Falls App nicht installiert → nach 1.5s zum AppStore weiterleiten
  const universal = buildUniversalLink(praxisCode);
  const deep = buildDeepLink(praxisCode);

  // visibilitychange-Trick: wenn der User die App geöffnet hat, wird die Seite versteckt
  let appOpened = false;
  const onHide = () => { appOpened = true; };
  document.addEventListener('visibilitychange', onHide);
  document.addEventListener('pagehide', onHide);

  // Versuche zuerst Universal-Link (funktioniert mit installierter App nahtlos)
  window.location.href = universal;

  // Nach 1500 ms: wenn die Seite noch sichtbar ist, App ist nicht installiert
  setTimeout(() => {
    document.removeEventListener('visibilitychange', onHide);
    document.removeEventListener('pagehide', onHide);
    if (appOpened) return;
    // Fallback: AppStore
    window.location.href = appStoreUrl();
  }, 1500);

  // Zusätzlicher Versuch: nativer Scheme (manche Browser blocken Universal Link)
  setTimeout(() => {
    if (!appOpened && document.visibilityState !== 'hidden') {
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = deep;
      document.body.appendChild(iframe);
      setTimeout(() => iframe.remove(), 100);
    }
  }, 300);
}

function showDesktopModal(praxisCode: string): void {
  // Modal mit QR-Code: User scannt mit Handy → öffnet App mit Praxis-Code
  const overlay = document.createElement('div');
  overlay.className = 'bertsy-overlay';
  overlay.innerHTML = `
    <div class="bertsy-modal">
      <button class="bertsy-modal-close" aria-label="Schließen">×</button>
      <div class="bertsy-modal-logo"></div>
      <h2>Mit Bertsy verbinden</h2>
      <p>
        Scannen Sie diesen QR-Code mit Ihrem Smartphone, um sich mit dieser Praxis zu verknüpfen.
        Falls Bertsy noch nicht installiert ist, werden Sie zum Download geleitet.
      </p>
      <div class="bertsy-qr-wrap">
        <img class="bertsy-qr" alt="QR-Code" />
      </div>
      <div class="bertsy-store-row">
        <a class="bertsy-store-link" href="${APP_STORE_URL_IOS}" target="_blank" rel="noopener">
          App Store (iOS)
        </a>
        <a class="bertsy-store-link" href="${APP_STORE_URL_ANDROID}" target="_blank" rel="noopener">
          Google Play (Android)
        </a>
      </div>
    </div>
  `;
  // Bertsy-Logo im Modal
  const logo = overlay.querySelector('.bertsy-modal-logo') as HTMLElement;
  if (logo) logo.innerHTML = logoSvg;
  // QR via Google Chart API (kostenlos, kein Library nötig)
  const qrTarget = buildUniversalLink(praxisCode);
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${encodeURIComponent(qrTarget)}`;
  const qrImg = overlay.querySelector('.bertsy-qr') as HTMLImageElement;
  if (qrImg) qrImg.src = qrUrl;

  const closeBtn = overlay.querySelector('.bertsy-modal-close') as HTMLButtonElement;
  closeBtn.addEventListener('click', () => overlay.remove());
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) overlay.remove();
  });

  document.body.appendChild(overlay);
}

function open(praxisCode: string): void {
  if (!praxisCode) {
    console.error('[Bertsy] data-praxis-code fehlt am Script-Tag.');
    return;
  }
  if (isMobile()) {
    openOnMobile(praxisCode);
  } else {
    showDesktopModal(praxisCode);
  }
}

function mount(): void {
  if (document.querySelector('[data-bertsy-root]')) return;

  const opts = getScriptOptions();
  const code = opts.praxisCode;

  if (!code) {
    console.error('[Bertsy-Embed] data-praxis-code ist erforderlich. Im Admin-Panel unter Praxen → QR-Code zu finden.');
    return;
  }

  const primary = opts.color ?? '#00A099';
  const pos = opts.position === 'bottom-left' ? 'left' : 'right';
  const label = opts.label ?? 'Mit Bertsy verbinden';

  document.documentElement.style.setProperty('--bertsy-primary', primary);

  const fab = document.createElement('button');
  fab.className = `bertsy-fab bertsy-pos-${pos}`;
  fab.setAttribute('aria-label', label);
  fab.title = label;
  fab.setAttribute('data-bertsy-root', '');
  fab.innerHTML = logoSvg;
  fab.addEventListener('click', () => open(code));

  document.body.appendChild(fab);

  window.Bertsy = { open: () => open(code) };
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', mount);
} else {
  mount();
}
